// Cookie consent state — minimal RGPD-compliant consent manager.
// Stored in localStorage (no third-party SaaS, no tracker).

export type ConsentChoice = 'accepted' | 'rejected' | 'pending'

export type ConsentState = {
  analytics: boolean
  /** ISO timestamp of when the choice was made */
  decidedAt: string | null
}

const STORAGE_KEY = 'yaslan_consent'
const VERSION = 1
const OPEN_EVENT = 'yaslan:openCookiePreferences'
const CHANGE_EVENT = 'yaslan:consentChanged'

type StoredPayload = {
  v: number
  analytics: boolean
  decidedAt: string
}

export function readConsent(): ConsentState {
  if (typeof window === 'undefined') {
    return { analytics: false, decidedAt: null }
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return { analytics: false, decidedAt: null }
    const parsed = JSON.parse(raw) as StoredPayload
    if (parsed.v !== VERSION) return { analytics: false, decidedAt: null }
    return { analytics: !!parsed.analytics, decidedAt: parsed.decidedAt }
  } catch {
    return { analytics: false, decidedAt: null }
  }
}

export function writeConsent(analytics: boolean) {
  if (typeof window === 'undefined') return
  const payload: StoredPayload = {
    v: VERSION,
    analytics,
    decidedAt: new Date().toISOString(),
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: payload }))
}

export function clearConsent() {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(STORAGE_KEY)
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

/** Open the preferences panel from anywhere (e.g. cookie page button, footer link). */
export function openCookiePreferences() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(OPEN_EVENT))
}

export const CONSENT_EVENTS = {
  open: OPEN_EVENT,
  change: CHANGE_EVENT,
}
